import type {
  MedicationResponse,
  PatientMedicationResponse,
} from '../../services/api';
import { parseISOToLocalDate } from '../../utils/dateUtils';

/**
 * Pluralize a unit based on the amount.
 */
export const pluralize = (
  amount: number,
  singular: string,
  plural?: string
): string => {
  return Number(amount) === 1 ? singular : (plural ?? singular + 's');
};

/**
 * Determine the dosage unit based on the medication's form and strength.
 */
export const getDosageUnit = (
  medication: MedicationResponse | null,
  amount?: number
): string => {
  if (!medication) return '';

  const form = medication.form?.toLowerCase() ?? '';
  const strength = medication.strength?.toLowerCase() ?? '';
  const n = amount ?? 2; // Default to plural if amount not provided

  // Tablets and similar solid forms
  if (form.includes('tablet')) return pluralize(n, 'tablet');
  if (form.includes('capsule')) return pluralize(n, 'capsule');
  if (form.includes('lozenge')) return pluralize(n, 'lozenge');
  if (form.includes('suppository'))
    return pluralize(n, 'suppository', 'suppositories');
  if (form.includes('patch')) return pluralize(n, 'patch', 'patches');

  // Liquid forms - extract unit from strength if possible
  if (
    form.includes('solution') ||
    form.includes('syrup') ||
    form.includes('suspension') ||
    form.includes('elixir') ||
    form.includes('liquid')
  ) {
    // Try to extract unit from strength (e.g., "160MG/5ML" -> "mL")
    if (strength.includes('/ml') || strength.includes('/ ml')) return 'mL';
    return 'mL';
  }

  // Injectable forms
  if (form.includes('injection') || form.includes('injectable')) {
    return 'mL';
  }

  // Topical forms
  if (
    form.includes('cream') ||
    form.includes('ointment') ||
    form.includes('gel')
  ) {
    return pluralize(n, 'application');
  }

  // Inhalers
  if (form.includes('inhaler') || form.includes('aerosol')) {
    return pluralize(n, 'puff');
  }

  // Drops
  if (form.includes('drop')) {
    return pluralize(n, 'drop');
  }

  // Default
  return pluralize(n, 'dose');
};

/**
 * Format a date string (YYYY-MM-DD) for display
 */
export const formatDate = (dateStr: string): string => {
  const date = parseISOToLocalDate(dateStr)!;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const getMedicationDisplayName = (
  med: PatientMedicationResponse
): string => {
  if (med.is_off_market) {
    return med.off_market_description ?? 'Off-market medication';
  }
  return med.medication?.full_name ?? 'Unknown medication';
};

export const getMedicationDosageDisplay = (
  med: PatientMedicationResponse
): string => {
  if (med.is_nonstandard_dosage && med.nonstandard_doses?.length) {
    return med.nonstandard_doses.join('; ');
  }
  if (med.amount) {
    const numAmount = Number(med.amount);
    const unit = med.medication
      ? getDosageUnit(med.medication, numAmount)
      : pluralize(numAmount, 'dose');
    return `${med.amount} ${unit}`;
  }
  return '';
};

/**
 * Check if a medication is actually discontinued (discontinue date is today or past)
 */
export const isActuallyDiscontinued = (
  med: PatientMedicationResponse
): boolean => {
  if (!med.discontinue_date) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const discDate = parseISOToLocalDate(med.discontinue_date)!;
  return discDate <= today;
};
